// Resolve a placement key to its `/go/<slug>` short link.
//
// The slugs in golinks.json are minted by the engine (dharmaweave/chakra/links.py),
// not by this file — see scripts/mint-site-links.py. The site only ever carries
// the slug. Two reasons that matters:
//
//   1. One brain per lever. links.py owns every utm_* this studio writes. A
//      second component composing its own campaign string is how one campaign
//      ends up with two names and a ledger that splits its own evidence in half.
//
//   2. The tag can be corrected after publish. `/go` resolves at click time, so
//      links.bind_surface() can re-point a live link. A raw tagged URL baked into
//      a page (or worse, a caption) is permanently whatever it was on the day.
//
// The click is counted at our own door by functions/go/[slug].js, which is the
// only honest number we will ever have for "left the page" — Amazon, the App
// Store and Play all strip our parameters on arrival.

import golinks from "@/data/golinks.json";

/**
 * `/go/<slug>` for a placement key, or null if that key was never minted.
 *
 * Returns a root-relative path so it works on localhost, on a Pages preview
 * deployment and in production without a build-time origin.
 *
 * @param {string} key e.g. "magnet-bio-instagram" or "x-padmasambhava"
 */
export function goPath(key) {
  const slug = golinks?.slugs?.[key];
  return slug ? `/go/${slug}` : null;
}

/** Absolute `/go` URL — for anything that leaves the page (share sheets, QR, copy). */
export function goUrl(key, origin) {
  const path = goPath(key);
  if (!path) return null;
  const base =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : golinks?.site) ||
    "";
  return `${base}${path}`;
}

/** The offer id the current golinks.json was minted against. */
export function mintedOfferId() {
  return golinks?.offer_id || null;
}

/**
 * The one tagged outbound link an /x article is allowed to carry.
 *
 * The strategy gives each article exactly one — "each footer carries exactly one
 * tagged link to the primary SKU or /go". Falling back to the offer route
 * un-tagged would be worse than useless: it would look measured and not be, and
 * a link that looks measured is how 1,999 posts produced zero attributable
 * sales. So an unminted article gets the plain route and says so in dev.
 */
export function articleGoPath(articleId, offerPath = "/offer") {
  const p = goPath(`x-${articleId}`);
  if (p) return p;
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      `[golinks] no minted /go link for article "${articleId}" — run scripts/mint-site-links.py. ` +
        `Falling back to ${offerPath}, which is NOT attributable.`
    );
  }
  return offerPath;
}
